/**
 * @name FunctionUtils.ts
 * @version 2.0.0
 * @description Utilidades para manejo de números y operaciones matemáticas
 * @author
 * @changelog
 * 2.0.0 - 2024/12/19 - Refactorización para eliminar código duplicado
 * 1.0.0 - 2024/12/19 - Versión inicial
 */

// ============================================================================
// FUNCIONES HELPER PRIVADAS
// ============================================================================

/**
 * Valida si un número es válido (no es NaN ni null)
 */
function _isValidNumber(number: number): boolean {
	return !isNaN(number) && number != null
}

/**
 * Establece el valor por defecto para numDecimals
 */
function _getDefaultDecimals(numDecimals: number | undefined): number {
	return numDecimals || 2
}

/**
 * Convierte un número a array [parte entera, parte decimal]
 */
function _splitNumber(number: number): [string, string] {
	const numberStr = number.toString()
	const parts = numberStr.split('.')
	return [parts[0], parts[1] || '']
}

/**
 * Genera una cadena de ceros de la longitud especificada
 */
function _generateZeros(length: number): string {
	return Array.from({ length }, () => '0').join('')
}

/**
 * Crea el multiplicador para redondear a un número específico de decimales
 */
function _createMultiplier(numDecimals: number): number {
	return Number('1' + _generateZeros(numDecimals))
}

// ============================================================================
// FUNCIONES PÚBLICAS
// ============================================================================

/**
 * Completa un número con ceros hasta el número de decimales especificado
 */
export function completeZeros(number: number, arrayNumbersNoTransform: number[], numDecimals: number) {
	if (!_isValidNumber(number)) {
		return number
	}
	numDecimals = _getDefaultDecimals(numDecimals)
	if (arrayNumbersNoTransform && arrayNumbersNoTransform.constructor.name == 'Array' && arrayNumbersNoTransform.indexOf(Number(number)) >= 0) {
		return Number(number).toString()
	}
	const [integerPart, decimalPart] = _splitNumber(number || 0)
	const zeros = _generateZeros(numDecimals - decimalPart.length)
	const decimals = decimalPart + zeros
	return integerPart + '.' + decimals
}

/**
 * Corrige errores de precisión en números flotantes
 */
export function fixPressisionError(number: number, numDecimals: number) {
	if (!_isValidNumber(number)) {
		throw new Error(`El número no es válido ${number}`)
	}
	const [, decimalPart] = _splitNumber(number)
	if (decimalPart.length === 0) {
		return number
	}
	if (decimalPart.length < 12) {
		return number
	}
	const counters = {
		zeros: 0,
		nines: 0
	}
	for (const element of decimalPart) {
		if (element === '9') {
			counters.nines++
		}
		if (element === '0') {
			counters.zeros++
		}
	}
	numDecimals = _getDefaultDecimals(numDecimals)
	const multiplier = _createMultiplier(numDecimals)
	const complement09 = 3
	if (counters.zeros >= decimalPart.length - complement09 || counters.nines >= decimalPart.length - complement09) {
		return Math.round(number * multiplier) / multiplier
	}
	return number
}

/**
 * Trunca un número a un número específico de posiciones decimales
 */
export function truncate(number: number, posiciones = 2) {
	const completeDecimals = _generateZeros(posiciones)
	const [integerPart, decimalPart] = _splitNumber(number)
	const decimals = decimalPart + completeDecimals
	if (!decimalPart) {
		return integerPart
	}
	return integerPart + '.' + decimals.slice(0, posiciones)
}
